import { Migration } from '@gleamer/types';
import classNames from 'classnames';
import React from 'react';
import { TableBodyTd, TableHeadTh } from '../../../atoms/Table';
import { formatDateTime } from '../imports/format';
import { ProgressBar } from '../imports/ProgressBar';
import { CommandButton, CountStatusBadge, formatCount } from './parts';

type MigrationRun = Migration.MigrationRun;
type MigrationGroupProgress = Migration.MigrationGroupProgress;
type MigrationKindCount = Migration.MigrationKindCount;
type MigrationCountStatus = Migration.MigrationCountStatus;

/** How the kinds are laid out: under the group that copies them, or under what their counts say. */
export type KindsGrouping = 'group' | 'status';

/** The statuses in the order they are shown: what needs looking at first. */
const STATUS_ORDER: MigrationCountStatus[] = [
  'DIFFERS',
  'COUNTING',
  'COPYING',
  'EXPLAINED',
  'SAME',
  'NOT_MIGRATED',
];

const COLUMNS = 9;

/** Whether a run's copy is over — done, with errors or not, stopped or failed —: its tables are counted, as migration-service does. */
export function copyIsOver(state: Migration.MigrationRunState) {
  return (
    state === 'DONE' ||
    state === 'DONE_WITH_ERRORS' ||
    state === 'STOPPED' ||
    state === 'FAILED'
  );
}

/** A summary of a run's counts: how many kinds agree out of those migrated, how many differ, how many are not in yet. */
export function countsSummary(counts: MigrationKindCount[]) {
  const migrated = counts.filter(c => c.status !== 'NOT_MIGRATED');
  return {
    migrated: migrated.length,
    agree: migrated.filter(c => c.status === 'SAME' || c.status === 'EXPLAINED')
      .length,
    differ: migrated.filter(c => c.status === 'DIFFERS').length,
    pending: migrated.filter(
      c => c.status === 'COUNTING' || c.status === 'COPYING'
    ).length,
  };
}

/** A kind's row: the copiers' counters (of one group, or summed), and the kind's counts when the run is counted. */
type Row = {
  kind: string;
  read: number | null;
  written: number | null;
  skipped: number | null;
  /** The copier's step and how many segments it is done in, for a row under its group. */
  progress?: { step: number; segmentsDone: number };
  count?: MigrationKindCount;
};

/** A cell in a failing comparison's colour; a null count is shown as a dash. */
function Count({
  value,
  off,
  title,
}: {
  value: number | null | undefined;
  off?: boolean;
  title?: string;
}) {
  return (
    <TableBodyTd
      className={classNames(
        'text-right tabular-nums',
        off && 'font-bold text-dl-warning'
      )}
      title={title}
    >
      {value === null || value === undefined ? '—' : formatCount(value)}
    </TableBodyTd>
  );
}

function KindRow({ row, sharedWith }: { row: Row; sharedWith?: string }) {
  const c = row.count;
  const failing = new Set(c?.checks ?? []);
  const across = sharedWith ? `Across ${sharedWith}` : undefined;
  return (
    <>
      <tr data-testid={`kind-${row.kind}`}>
        <TableBodyTd
          className="pl-8 font-bold"
          title={
            row.progress
              ? `Step ${row.progress.step}; done in ${row.progress.segmentsDone} segment(s)`
              : c?.group
          }
        >
          {row.kind}
        </TableBodyTd>
        <TableBodyTd className="font-mono text-sm">
          {c ? (c.targetTable ?? '—') : ''}
        </TableBodyTd>
        <Count
          value={c?.expected}
          off={
            failing.has('read ≠ expected') ||
            failing.has('in PostgreSQL ≠ expected')
          }
          title={
            c?.expectedError ??
            (c?.expectedAt
              ? `${c.expectedMethod === 'STATISTICS' ? 'Datastore statistics of' : 'Counted at'} ${formatDateTime(c.expectedAt)}${across ? ` — ${across}` : ''}`
              : across)
          }
        />
        <Count
          value={row.read}
          off={
            failing.has('read ≠ expected') ||
            failing.has('written + skipped ≠ read')
          }
        />
        <Count
          value={row.written}
          off={
            failing.has('in PostgreSQL ≠ written') ||
            failing.has('written + skipped ≠ read')
          }
        />
        <Count
          value={row.skipped}
          off={
            failing.has('skipped ≠ recorded errors') ||
            (!c && (row.skipped ?? 0) > 0)
          }
        />
        <Count
          value={c?.errors}
          off={failing.has('skipped ≠ recorded errors')}
          title={across}
        />
        <Count
          value={c?.inPostgres}
          off={
            failing.has('in PostgreSQL ≠ written') ||
            failing.has('in PostgreSQL ≠ expected')
          }
          title={
            c?.inPostgresError ??
            (c?.inPostgresAt
              ? `Counted at ${formatDateTime(c.inPostgresAt)}`
              : across)
          }
        />
        <TableBodyTd>
          {c && (
            <CountStatusBadge
              status={c.status}
              title={c.checks.join(', ') || undefined}
            />
          )}
          {c?.expectedMethod === 'STATISTICS' && (
            <span className="ml-1 text-xs text-dl-faint">(statistics)</span>
          )}
        </TableBodyTd>
      </tr>
      {c && (c.checks.length > 0 || c.note) && (
        <tr>
          <td
            colSpan={COLUMNS}
            className="pb-2 pl-8 pr-4 text-sm text-dl-muted"
          >
            {c.checks.length > 0 && (
              <span className="font-bold text-dl-warning">
                {c.checks.join(' · ')}
              </span>
            )}
            {c.checks.length > 0 && c.note && ' — '}
            {c.note}
          </td>
        </tr>
      )}
    </>
  );
}

/** A section's header row, across the table. */
function SectionRow({
  children,
  testId,
}: React.PropsWithChildren<{ testId: string }>) {
  return (
    <tr className="bg-dl-sunken" data-testid={testId}>
      <td colSpan={COLUMNS} className="px-4 py-2">
        <div className="flex flex-wrap items-center gap-3">{children}</div>
      </td>
    </tr>
  );
}

function GroupHeader({
  run,
  group,
  canReplay,
}: {
  run: MigrationRun;
  group: MigrationGroupProgress;
  canReplay: boolean;
}) {
  const s = group.segments;
  return (
    <SectionRow testId={`group-${group.group}`}>
      <span className="w-24 font-bold">{group.group}</span>
      <div className="w-40">
        <ProgressBar
          label={`${group.group} segments`}
          segments={[
            { label: 'Done', value: s.done, className: 'bg-dl-success' },
            { label: 'Failed', value: s.failed, className: 'bg-dl-danger' },
            { label: 'Running', value: s.running, className: 'bg-dl-primary' },
            { label: 'Pending', value: s.pending, className: 'bg-dl-faint' },
          ]}
        />
      </div>
      <span className="text-sm">
        {s.done} / {s.total} segments
        {s.running > 0 && `, ${s.running} running`}
        {s.failed > 0 && (
          <span className="text-dl-danger">, {s.failed} failed</span>
        )}
      </span>
      <span className="ml-auto">
        {canReplay && s.running === 0 && (
          <CommandButton
            label="Replay group"
            confirm={`Lay ${group.group} out again from its first key? Its segments, cursors and errors are dropped.`}
            command={{ type: 'REPLAY', runId: run.id, group: group.group }}
          />
        )}
        {canReplay && s.running > 0 && (
          <span className="text-sm text-dl-faint">
            Pause the run to replay it
          </span>
        )}
      </span>
    </SectionRow>
  );
}

/**
 * A run's kinds, in one table: the copiers' counters, and — for a whole bulk run — what Datastore held when it started,
 * the errors recorded and the rows the tables hold once it is over, with whether they agree. Laid out under the group
 * that copies them, with each group's segments and its replay, or under what their counts say, the differences first.
 */
export function MigrationKindsSection({
  run,
  groups,
  counts,
  grouping,
  onGrouping,
  canReplay,
}: {
  run: MigrationRun;
  groups: MigrationGroupProgress[];
  counts: MigrationKindCount[];
  grouping: KindsGrouping;
  onGrouping: (grouping: KindsGrouping) => void;
  canReplay: boolean;
}) {
  const byKind = new Map(counts.map(c => [c.kind, c]));
  const counted = counts.length > 0;
  const shown: KindsGrouping = counted ? grouping : 'group';
  const groupsOf = new Map<string, string[]>();
  groups.forEach(g =>
    g.kinds.forEach(k =>
      groupsOf.set(k.kind, [...(groupsOf.get(k.kind) ?? []), g.group])
    )
  );

  let body: React.ReactNode;
  if (shown === 'group') {
    const notCopied = counts.filter(c => !groupsOf.has(c.kind));
    body = (
      <>
        {groups.map(group => (
          <React.Fragment key={group.group}>
            <GroupHeader run={run} group={group} canReplay={canReplay} />
            {group.kinds.length === 0 && (
              <tr>
                <TableBodyTd colSpan={COLUMNS} className="pl-8 text-dl-faint">
                  Not laid out yet
                </TableBodyTd>
              </tr>
            )}
            {group.kinds.map(k => {
              const shared = groupsOf.get(k.kind) ?? [];
              return (
                <KindRow
                  key={`${group.group}/${k.kind}`}
                  row={{
                    kind: k.kind,
                    read: k.read,
                    written: k.written,
                    skipped: k.skipped,
                    progress: { step: k.step, segmentsDone: k.segmentsDone },
                    count: byKind.get(k.kind),
                  }}
                  sharedWith={shared.length > 1 ? shared.join(', ') : undefined}
                />
              );
            })}
          </React.Fragment>
        ))}
        {notCopied.length > 0 && (
          <>
            <SectionRow testId="group-not-copied">
              <span className="font-bold">Not copied</span>
              <span className="text-sm text-dl-muted">
                no copier reads these kinds: written by another&apos;s, rebuilt,
                or left in g3d
              </span>
            </SectionRow>
            {notCopied.map(c => (
              <KindRow
                key={c.kind}
                row={{
                  kind: c.kind,
                  read: c.read,
                  written: c.written,
                  skipped: c.skipped,
                  count: c,
                }}
              />
            ))}
          </>
        )}
      </>
    );
  } else {
    body = STATUS_ORDER.map(status => {
      const kinds = counts.filter(c => c.status === status);
      if (kinds.length === 0) {
        return null;
      }
      return (
        <React.Fragment key={status}>
          <SectionRow testId={`status-${status}`}>
            <CountStatusBadge status={status} />
            <span className="text-sm">{kinds.length} kind(s)</span>
          </SectionRow>
          {kinds.map(c => (
            <KindRow
              key={c.kind}
              row={{
                kind: c.kind,
                read: c.read,
                written: c.written,
                skipped: c.skipped,
                count: c,
              }}
            />
          ))}
        </React.Fragment>
      );
    });
  }

  return (
    <section aria-labelledby="kinds-title">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h3 id="kinds-title" className="text-xl font-bold">
          Kinds
        </h3>
        {counted && (
          <div
            role="group"
            aria-label="Group the kinds"
            className="flex rounded-full border border-dl-border bg-dl-sunken p-0.5 text-sm"
          >
            {(['group', 'status'] as KindsGrouping[]).map(option => (
              <button
                key={option}
                type="button"
                aria-pressed={shown === option}
                onClick={() => onGrouping(option)}
                className={classNames(
                  'rounded-full px-3 py-1 transition-colors',
                  shown === option
                    ? 'bg-dl-surface font-bold text-dl-text shadow-sm'
                    : 'text-dl-muted hover:text-dl-text'
                )}
              >
                {option === 'group' ? 'By group' : 'By status'}
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="mb-2 text-sm text-dl-muted">
        {counted
          ? "Each kind: Datastore's count when the run started, what the copiers read, wrote and skipped, the errors recorded, and the rows its table holds once the run is over."
          : 'What the copiers read, wrote and skipped. Only whole bulk runs are counted against Datastore and the tables.'}
      </p>
      {/* contain: the worklist surface is as wide as its content's min-content, and the table's columns would otherwise
          widen the page past the screen (and the header with it); the table scrolls inside instead */}
      <div className="overflow-x-auto [contain:inline-size]">
        <table className="w-full table-auto">
          <thead className="bg-dl-track">
            <tr>
              <TableHeadTh>Kind</TableHeadTh>
              <TableHeadTh>Table</TableHeadTh>
              <TableHeadTh>Expected</TableHeadTh>
              <TableHeadTh>Read</TableHeadTh>
              <TableHeadTh>Written</TableHeadTh>
              <TableHeadTh>Skipped</TableHeadTh>
              <TableHeadTh>Errors</TableHeadTh>
              <TableHeadTh>In PostgreSQL</TableHeadTh>
              <TableHeadTh>Status</TableHeadTh>
            </tr>
          </thead>
          <tbody>{body}</tbody>
        </table>
      </div>
    </section>
  );
}
